import { createValueFormatter } from '@cmg/data-grid';

import { OfferingType } from '../../../../../../graphql/__generated__';
import type {
  CalendarGridColDef,
  CalendarGridContext,
  CalendarGridFilterTreeListPathGetter,
  CalendarGridKeyCreatorParams,
  CalendarGridRowData,
  CalendarGridSetFilterParams,
  CalendarGridSetFilterValuesFunc,
} from '../../CalendarGrid.model';

export type Value = CalendarGridRowData['typeDisplayName'];

type FilterGroup = 'IPO' | 'Follow-on' | 'Block';

export type SetFilterValue = [FilterGroup, string] | [string];

/**
 * !!! TEMPORARY FIX !!!
 * This can be removed once the backend is updated to return correct `type` for IPO SPAC offerings.
 */
const isIpoSpac = (data: CalendarGridRowData | null | undefined) => {
  return data?.attributes?.isSpac && data.type === OfferingType.Ipo;
};

const FILTER_TREE_STRUCTURE_MAP = new Map<OfferingType, FilterGroup | null>([
  [OfferingType.Ipo, 'IPO'],
  [OfferingType.IpoSpac, 'IPO'],
  [OfferingType.DirectListing, 'IPO'],
  [OfferingType.FollowOn, 'Follow-on'],
  [OfferingType.MarketedFo, 'Follow-on'],
  [OfferingType.OvernightFo, 'Follow-on'],
  [OfferingType.Abb, 'Follow-on'],
  [OfferingType.UnderwrittenRd, 'Follow-on'],
  [OfferingType.UnregisteredBlock, 'Block'],
  [OfferingType.RegisteredBlock, 'Block'],
  [OfferingType.Convertible, null],
  [OfferingType.Rights, null],
  [OfferingType.RegisteredDirect, null],
]);

const FILTER_GROUP_ITEMS_ORDER = [...FILTER_TREE_STRUCTURE_MAP.entries()];

const getFilterGroupIndex = (filterGroup: FilterGroup | null, filterValue: OfferingType) => {
  return FILTER_GROUP_ITEMS_ORDER.findIndex(
    ([offeringType, group]) => offeringType === filterValue && group === filterGroup
  );
};
export const setFilterValuesFunc: CalendarGridSetFilterValuesFunc<SetFilterValue> = ({
  success,
  context,
}) => {
  const offeringTypes = (context as CalendarGridContext).offeringTypes;
  const filterOptions = offeringTypes
    .map(({ value, label }) => {
      const filterGroup = FILTER_TREE_STRUCTURE_MAP.get(value) ?? null;
      return { label, value, filterGroup };
    })
    .sort((lhs, rhs) => {
      return (
        getFilterGroupIndex(lhs.filterGroup, lhs.value) -
        getFilterGroupIndex(rhs.filterGroup, rhs.value)
      );
    })
    .map<SetFilterValue>(({ filterGroup, label }) => {
      return filterGroup ? [filterGroup, label] : [label];
    });

  success(filterOptions);
};

export const setFilterKeyCreator = ({
  data,
  ...params
}: CalendarGridKeyCreatorParams<SetFilterValue>) => {
  let value = params.value;

  if (data?.typeDisplayName) {
    const filterGroup = FILTER_TREE_STRUCTURE_MAP.get(data.type);
    /**
     * !!! TEMPORARY FIX !!!
     * This can be removed once the backend is updated to return correct `type` for IPO SPAC offerings.
     */
    const typeDisplayName = isIpoSpac(data)
      ? `${data.typeDisplayName} (SPAC)`
      : data.typeDisplayName;

    value = filterGroup ? [filterGroup, typeDisplayName] : [typeDisplayName];
  }

  return value?.join('-') ?? '';
};

export const setFilterTreeListPathGetter: CalendarGridFilterTreeListPathGetter<
  SetFilterValue
> = value => value;

/**
 * !!! TEMPORARY FIX !!!
 * This can be removed once the backend is updated to return correct `type` for IPO SPAC offerings.
 */
export const valueFormatter = createValueFormatter<CalendarGridRowData, Value>({
  gridRow: ({ data, value }) => {
    if (!value) {
      return '-';
    }

    return isIpoSpac(data) ? `${value} (SPAC)` : value;
  },
});

const offeringTypeColDef: CalendarGridColDef<Value> = {
  field: 'typeDisplayName',
  width: 135,
  headerName: 'Offering Type',
  type: 'text',
  valueFormatter,
  filter: 'agSetColumnFilter',
  filterParams: {
    treeList: true,
    values: setFilterValuesFunc,
    keyCreator: setFilterKeyCreator,
    treeListPathGetter: setFilterTreeListPathGetter,
    suppressSorting: true,
  } satisfies CalendarGridSetFilterParams<SetFilterValue>,
};

export default offeringTypeColDef;
